/**
 * Featurebase state shared with components: whether the Provider is mounted
 * (FeaturebaseAvailableContext, used by the header MessagesButton) and the
 * What's new widget (FeaturebaseChangelogContext, used by the Home
 * "See what's new" button).
 *
 * Only ConditionalFeaturebaseProvider sets these. Every featurebase-js export
 * loads Featurebase's script on first call, so components must check these
 * contexts rather than calling the SDK directly: when Featurebase is switched
 * off (or not ready yet) they render nothing and the script is never loaded.
 */
import { createContext, useContext } from 'react'

export interface FeaturebaseChangelogApi {
  /** Open the full-screen updates popup. */
  openPopup: () => void
  /** Mark every update as read for this browser. */
  markAllAsViewed: () => void
  /**
   * Unread update count. `undefined` until the widget is up, then `0` until
   * the updates arrive — so `0` can mean loading, failed or none.
   */
  getUnviewedCount: () => number | undefined
}

/**
 * True only beneath the enabled Provider. Messenger hooks (useFeaturebase())
 * must only run where this is true.
 */
export const FeaturebaseAvailableContext = createContext(false)

export function useIsFeaturebaseAvailable(): boolean {
  return useContext(FeaturebaseAvailableContext)
}

export const FeaturebaseChangelogContext =
  createContext<FeaturebaseChangelogApi | null>(null)

export function useFeaturebaseChangelog(): FeaturebaseChangelogApi | null {
  return useContext(FeaturebaseChangelogContext)
}

/** App-wide "unread updates" flag, shared by every button that shows it. */
export interface UnreadUpdatesState {
  /** True once an unread update has been seen in this page load. */
  hasUnreadUpdates: boolean
  /** How many updates were unread when last checked (0 once marked read). */
  unreadUpdatesCount: number
  /** Mark every update read in Featurebase and clear the flag everywhere. */
  markUpdatesRead: () => void
}

export const FeaturebaseUnreadUpdatesContext =
  createContext<UnreadUpdatesState>({
    hasUnreadUpdates: false,
    unreadUpdatesCount: 0,
    markUpdatesRead: () => {},
  })
